"use client";

import { Carousel, CarouselContent, CarouselItem } from "@repo/ui/carousel";
import { Icon } from "@repo/ui/icon";
import { Link } from "@/i18n/navigation";
import { ListingCard } from "@/components/cards/listing-card";
import type { ListingRailCard } from "./listing-rail.helpers";

export interface ListingRailSeeAll {
  label: string;
  href: string;
}

interface ListingRailCarouselProps {
  items: ListingRailCard[];
  seeAll?: ListingRailSeeAll | null;
}

// Fluid widths matching the food grid's container-grid col-span-4 per
// breakpoint, so cards stay the same size as the editorial cards (VAW-1303).
const itemClass =
  "basis-[min(85vw,323px)] pl-3 tablet:pl-6 tablet:basis-[calc((100vw_-_208px)/2_+_96px)] desktop:basis-[calc((min(100vw,1600px)_-_392px)/3_+_96px)]";

export function ListingRailCarousel({
  items,
  seeAll,
}: ListingRailCarouselProps) {
  return (
    <Carousel
      align="start"
      duration={40}
      dragFree
      wheelGestures
      aria-label="Listing rail"
      data-testid="listing-rail-carousel"
    >
      <CarouselContent
        viewportClassName="pl-4 desktop:pl-16 desktop-lg:pl-[max(4rem,calc((100vw-1600px)/2+4rem))]"
        className="-ml-3 tablet:-ml-6"
      >
        {items.map(({ key, props }) => (
          <CarouselItem key={key} className={itemClass}>
            <ListingCard {...props} />
          </CarouselItem>
        ))}
        {seeAll && (
          <CarouselItem className={itemClass}>
            {/* End-cap tile sized like the card image area */}
            <Link
              href={seeAll.href}
              data-testid="listing-rail-see-all"
              className="group flex aspect-[358/245] w-full items-center justify-center gap-2 rounded-sm border border-border-default text-text-primary transition-colors hover:border-border-active desktop:aspect-[421/289] desktop:rounded-md"
            >
              <span className="typography-component-sm-medium desktop:typography-component-md-medium">
                {seeAll.label}
              </span>
              <Icon
                name="arrow-right"
                className="size-4 transition-transform group-hover:translate-x-1 desktop:size-5"
              />
            </Link>
          </CarouselItem>
        )}
        {/* Trailing spacer so the last card isn't flush with the screen edge */}
        <div
          aria-hidden
          className="shrink-0 grow-0 basis-4 desktop:basis-16 desktop-lg:basis-[max(4rem,calc((100vw-1600px)/2+4rem))]"
        />
      </CarouselContent>
    </Carousel>
  );
}
